import React, { useState, useRef, useEffect } from 'react';
import { useCrumbChat } from '../hooks/useCrumbChat';
import CrumbAvatar from '../components/CrumbAvatar';

/** User avatar */
const USER_AVATAR_SRC = './assets/user-avatar.png';

const UserAvatar = ({ size = 'md', className = '' }) => {

  const sizeClasses =
    size === 'lg' ? 'w-[77px] h-[96px] text-5xl' :
    size === 'md' ? 'w-[52px] h-[60px] text-4xl' :
    'w-[30px] h-[36px] text-2xl';
	return (
  <img src={USER_AVATAR_SRC} alt="user" className={`flex items-center justify-center ${sizeClasses} select-none pointer-events-none ${className}`}
      aria-hidden
      title="user"
      style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,.25))' }} />
	);
};

const CrumbApp = ({ onClose }) => {
  const {
    messages,
    isTyping,
    isInitialized,
    currentStreamingMessageId,
    sendMessage,
    clearMessages,
    initializeChat
  } = useCrumbChat();

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  // Guard against StrictMode double-init
  const initOnceRef = useRef(false);
  useEffect(() => {
    if (!initOnceRef.current) {
      initOnceRef.current = true;
      if (!isInitialized) initializeChat();
    }
  }, [initializeChat, isInitialized]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, currentStreamingMessageId]);

  useEffect(() => {
    if (chatInputRef.current && isInitialized) {
      setTimeout(() => chatInputRef.current?.focus(), 100);
    }
  }, [isInitialized]);

  const handleInputChange = (e) => setInputValue(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inputValue.trim() === '' || isTyping) return;
    const userMessage = inputValue.trim();
    setInputValue('');
    try {
      await sendMessage(userMessage);
    } catch (error) {
      console.error('🧁 Error sending message:', error);
    }
  };

  const handleNewChat = () => {
    if (isTyping) return;
    clearMessages();
  };

  const handleSaveChat = () => {
    const now = new Date();
    const dateTimeForFilename = now.toISOString()
      .replace(/:/g, '-')
      .replace('T', '_')
      .slice(0, 19);

    const chatContent = [
      "🧁 Pink Pixel OS - Chat with Crumb\n",
      "=======================================\n\n",
      "# AI CHAT EXPORT\n",
      `Exported on: ${now.toLocaleString()}\n\n`,
      "## Conversation with Crumb 🧁\n",
      ...messages.map(msg => {
        const sender = msg.sender === 'user' ? 'You' : 'Crumb 🧁';
        const time = new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return `**${sender}** (${time}): ${msg.content}\n\n`;
      })
    ].join('');

    const blob = new Blob([chatContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crumb-chat-${dateTimeForFilename}.txt`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatMessageContent = (content) => {
    const paragraphs = content.split('\n\n');
    if (paragraphs.length <= 1) {
      const singleBreakParagraphs = content.split('\n');
      if (singleBreakParagraphs.length > 1) {
        return singleBreakParagraphs.map((paragraph, idx) => (
          <p key={idx} className="mb-2 last:mb-0">{paragraph}</p>
        ));
      }
      return content;
    }
    return paragraphs.map((paragraph, idx) => (
      <p key={idx} className="mb-3 last:mb-0">{paragraph}</p>
    ));
  };

  // Determine if the streaming message already has text
  const streamingMsg = currentStreamingMessageId
    ? messages.find(m => m.id === currentStreamingMessageId)
    : null;
  const streamingHasContent = !!(streamingMsg?.content && streamingMsg.content.trim());

  return (
    <div className="flex flex-col h-full min-w-[600px] min-h-[600px] bg-gradient-to-br from-pink-50/90 to-purple-50/90 backdrop-blur-md">
{/* Header */}
<div className="flex items-center justify-between p-4 border-b border-pink-200/30 bg-gradient-to-r from-pink-100/50 to-purple-100/50">
  {/* LEFT: avatar + titles */}
  <div className="flex items-center gap-3 flex-1 min-w-0">
    <CrumbAvatar className="w-24 h-24 md:w-[108px] md:h-[108px] drop-shadow-lg shrink-0" />
    <div className="min-w-0">
      <h2 className="text-xl font-semibold text-pink-800 truncate">Chat with Crumb🧁</h2>
      <p className="text-sm text-pink-600 truncate">Sugar Rush Dealer🍭</p>
    </div>
  </div>

  {/* RIGHT: buttons (don't'let these shrink the titles) */}
  <div className="flex items-center space-x-2 shrink-0">
          <button
            onClick={handleNewChat}
            disabled={isTyping}
            className="px-3 py-1 text-sm bg-pink-200/50 hover:bg-pink-300/50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200 text-pink-700 font-medium"
            title="Start a new chat">🍬 New</button>
          <button
            onClick={handleSaveChat}
            disabled={messages.length === 0}
            className="px-3 py-1 text-sm bg-purple-200/50 hover:bg-purple-300/50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200 text-purple-700 font-medium"
            title="Save chat as text file">💾 Save</button>
          )
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {!isInitialized && (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-2 text-pink-600">
              <div className="animate-bounce">
                {/* bump loader avatar too so it matches scale */}
                <CrumbAvatar className="w-[60px] h-[60px]" />
              </div>
              <span className="text-sm">Crumb is getting ready...</span>
            </div>
          </div>
        )}

        {messages.map((message) => {
          const isCrumb = message.sender !== 'user';
          const isStreaming = message.id === currentStreamingMessageId || message.isStreaming;
          const hasContent = !!(message.content && message.content.trim());

          // Hide empty streaming bubble so only typing indicator shows
          if (isCrumb && isStreaming && !hasContent) return null;

          return (
            <div
              key={message.id}
              className={`flex ${isCrumb ? 'justify-start' : 'justify-end'}`}
            >
              <div
                className={`max-w-xs lg:max-w-md xl:max-w-lg ${isCrumb ? 'order-1' : 'order-2'}`}
              >
                {/* Message Bubble */}
                <div
                  className={`rounded-2xl p-3 shadow-md ${
                    isCrumb
                      ? 'bg-gradient-to-br from-pink-200/80 to-purple-200/80 text-pink-800 backdrop-blur-sm'
                      : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white ml-auto'
                  }`}
                >
                  <div className="text-sm leading-relaxed">
                    {isCrumb && !message.isStreaming
                      ? formatMessageContent(message.content)
                      : message.content}
                    {message.id === currentStreamingMessageId && (
                      <span className="inline-block w-2 h-4 bg-pink-600 ml-1 animate-pulse"></span>
                    )}
                  </div>
                </div>

                {/* Timestamp */}
                <div className={`text-xs text-gray-500 mt-1 ${isCrumb ? 'text-left' : 'text-right'}`}>
                  {formatTimestamp(message.timestamp)}
                </div>
              </div>

              {/* Avatar (scaled up ~50%) */}
              <div className={`flex-shrink-0 ${isCrumb ? 'order-2 ml-2' : 'order-1 mr-2'}`}>
                {isCrumb ? (
                  isStreaming && hasContent
                    ? <div className="w-[72px] h-[72px]" /> // spacer to keep layout while typing indicator shows
                    : <CrumbAvatar className="w-[72px] h-[72px] drop-shadow" />
                ) : (
                  <UserAvatar size="md" />
                )}
              </div>
            </div>
          );
        })}

        {/* Typing Indicator only while no text yet in the streaming message */}
        {isTyping && (!currentStreamingMessageId || !streamingHasContent) && (
          <div className="flex justify-start">
            <div className="flex items-center space-x-2">
              <CrumbAvatar className="w-[60px] h-[60px] drop-shadow" />
              <div className="bg-gradient-to-br from-pink-200/80 to-purple-200/80 backdrop-blur-sm rounded-2xl px-4 py-3">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-pink-200/30 p-4 bg-gradient-to-r from-pink-100/30 to-purple-100/30">
        <form onSubmit={handleSubmit} className="flex space-x-2">
          <input
            ref={chatInputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Ask Crumb anything about Pink Pixel OS..."
            disabled={isTyping || !isInitialized}
            className="flex-1 px-4 py-2 bg-white/70 backdrop-blur-sm border border-pink-200 rounded-full focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 placeholder-gray-500"
          />
          <button
            type="submit"
            disabled={inputValue.trim() === '' || isTyping || !isInitialized}
            className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-full transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
          >
            {isTyping ? '💭' : '✨'}
          </button>
        </form>
        <div className="text-xs text-center text-gray-500 mt-2">
          Crumb is powered by AI. Please don't eat the messenger.
        </div>
      </div>
    </div>
  );
};

export default CrumbApp;
